import { Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, MouseEvent, useCallback, useMemo, VFC } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import { MonoSpan } from 'dashboard/components/Typography';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { WorkflowRun, WorkflowRunStatus } from 'dashboard/models/workflow-run';
import { WorkflowRunSimulated } from 'dashboard/models/workflow-run-simulated';
import Cell from 'dashboard/views/DashboardWorkflowRunsView/WorkflowRunsTable/Cell';
import WorkflowRunStatusSticker from 'dashboard/views/DashboardWorkflowRunsView/WorkflowRunsTable/WorkflowRunStatusSticker';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import NullStateWorkflowRuns from 'images/dashboard/i16-file-text.svg';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

const ACCOUNT_ACTION_RUNS_PATH =
  '/dashboard/account-types/:accountTypeId/actions/:accountActionId/runs';
const CASE_ACTION_RUNS_PATH = '/dashboard/case-actions/:actionWorkflowId/runs';

const StyledTD = styled(TableTD)<{ isDisabled?: boolean }>`
  ${({ isDisabled }) =>
    isDisabled &&
    `
    cursor: default;
    pointer-events: none;
  `}
`;

const ResolutionContainer = styled('div')`
  max-width: 400px;
`;

const WorkflowRunsTableContainer = styled('div')`
  margin-bottom: ${Spacing['2']};
`;

export enum CellType {
  Id = 'id',
  Status = 'status',
  Default = 'default',
}

export type ColumnConfig = {
  type: CellType;
  header: string;
  dataTest?: string;
  id?: string;
  sortParam?: string;
  linkToObject?: boolean;
  getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) => string;
};

interface WorkflowRunsTableProps {
  workflowRuns: (WorkflowRun | WorkflowRunSimulated)[];
  sortParams?: SortParams;
  isLoading: boolean;
  hasUsedFilters: boolean;
  columnConfigs: ColumnConfig[];
  handleSortUpdate: (sortParams: SortParams) => void;
  selectedRows?: Set<string>;
  setSelectedRows?: (selectedRows: Set<string>) => void;
}

const WorkflowRunsTable: VFC<WorkflowRunsTableProps> = ({
  workflowRuns,
  sortParams,
  isLoading,
  hasUsedFilters,
  columnConfigs,
  handleSortUpdate,
  selectedRows,
  setSelectedRows,
}) => {
  const { pathname } = useLocation();
  const matchCaseActionRunsRoute = matchPath(
    {
      path: CASE_ACTION_RUNS_PATH,
      end: false,
    },
    pathname,
  );
  const matchAccountActionRunsRoute = matchPath(
    {
      path: ACCOUNT_ACTION_RUNS_PATH,
      end: false,
    },
    pathname,
  );

  const withSelectableRows = selectedRows != null && setSelectedRows != null;
  const isWorkflowRunErrored = (run: WorkflowRun | WorkflowRunSimulated) =>
    [WorkflowRunStatus.Failed, WorkflowRunStatus.Errored].includes(run.attributes.status);

  const erroredWorkflowRuns = useMemo(
    () => workflowRuns.filter(isWorkflowRunErrored),
    [workflowRuns],
  );

  const allSelected = useMemo(() => {
    if (!withSelectableRows) return false;
    return (
      erroredWorkflowRuns.length > 0 &&
      erroredWorkflowRuns.length === selectedRows.size &&
      erroredWorkflowRuns.every((run) => selectedRows.has(run.id))
    );
  }, [erroredWorkflowRuns, selectedRows, withSelectableRows]);

  const toggleSelectAll = useCallback(() => {
    if (!withSelectableRows) {
      return;
    }
    allSelected
      ? setSelectedRows(new Set())
      : setSelectedRows(new Set(erroredWorkflowRuns.map(({ id }) => id)));
  }, [erroredWorkflowRuns, setSelectedRows, allSelected, withSelectableRows]);

  const handleCheckboxClick = useCallback(
    (
      e: MouseEvent | ChangeEvent<HTMLInputElement>,
      workflowRun: WorkflowRun | WorkflowRunSimulated,
    ) => {
      e.preventDefault();
      if (isWorkflowRunErrored(workflowRun)) {
        const newSelectedRows = new Set(selectedRows);
        newSelectedRows.has(workflowRun.id)
          ? newSelectedRows.delete(workflowRun.id)
          : newSelectedRows.add(workflowRun.id);

        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setSelectedRows(newSelectedRows);
      }
    },
    [selectedRows, setSelectedRows],
  );

  const maybeGetWorkflowTriggerObjectRoute = (id: string, type?: ApiModel) => {
    if (type == null) return null;

    const routeToModel: Partial<Record<ApiModel, string>> = {
      [ApiModel.Inquiry]: '/dashboard/inquiries',
      [ApiModel.Account]: '/dashboard/accounts',
      [ApiModel.Case]: '/dashboard/cases',
      [ApiModel.Transaction]: '/dashboard/transactions',
      [ApiModel.Report]: '/dashboard/reports',
    };

    return routeToModel[type] ? `${routeToModel[type]}/${id}` : null;
  };

  const workflowRunRows = workflowRuns.map((workflowRun) => {
    let workflowRunRoute;
    if (matchCaseActionRunsRoute) {
      workflowRunRoute = `/dashboard/case-actions/${workflowRun.relationships.workflow.data.id}/runs/${workflowRun.id}`;
    } else if (matchAccountActionRunsRoute) {
      const { accountTypeId, accountActionId } = matchAccountActionRunsRoute.params;
      workflowRunRoute = `/dashboard/account-types/${accountTypeId}/actions/${accountActionId}/runs/${workflowRun.id}`;
    } else {
      workflowRunRoute = `/dashboard/workflows/${workflowRun.relationships.workflow.data.id}/runs/${workflowRun.id}`;
    }

    return (
      <TableLinkTR
        key={workflowRun.id}
        data-test={`workflow-runs-table-v2__${workflowRun.id}`}
        to={workflowRunRoute}
      >
        {withSelectableRows && (
          <StyledTD
            id={`workflow-runs-table-v2__${workflowRun.id}__table-data-cell-checkbox`}
            onClick={(e) => handleCheckboxClick(e, workflowRun)}
            isDisabled={!isWorkflowRunErrored(workflowRun)}
          >
            <Checkbox
              id={`workflow-runs-table-v2__${workflowRun.id}__table-row-checkbox`}
              checked={selectedRows.has(workflowRun.id)}
              disabled={!isWorkflowRunErrored(workflowRun)}
              onChange={(e) => handleCheckboxClick(e, workflowRun)}
            />
          </StyledTD>
        )}
        {columnConfigs.map(({ type, getValue, header, linkToObject }, i) => {
          const value = getValue(workflowRun);
          const markup = (() => {
            switch (type) {
              case CellType.Id: {
                const link = maybeGetWorkflowTriggerObjectRoute(
                  value,
                  workflowRun.relationships['trigger-object']?.data?.type,
                );
                return (
                  <HoverCopyableField copyValue={value} confirmationText={`Copied ${header}`}>
                    {link && linkToObject ? (
                      <Link to={link}>
                        <MonoSpan>
                          <Cell text={value} />
                        </MonoSpan>
                      </Link>
                    ) : (
                      <MonoSpan>
                        <Cell text={value} />
                      </MonoSpan>
                    )}
                  </HoverCopyableField>
                );
              }
              case CellType.Status:
                return (
                  <ResolutionContainer>
                    <WorkflowRunStatusSticker workflowRunStatus={value as WorkflowRunStatus} />
                  </ResolutionContainer>
                );
              case CellType.Default:
                return <Cell text={value} />;
              default:
                assertNever(type);
            }
          })();
          return <TableTD key={`${workflowRun.id}_${i}`}>{markup}</TableTD>;
        })}
      </TableLinkTR>
    );
  });

  return (
    <WorkflowRunsTableContainer>
      <TableLayout
        dataTest='workflow-runs-table-v2'
        isLoading={isLoading}
        isEmpty={workflowRuns.length === 0}
        nullState={
          <TableNullState hasUsedFilters={hasUsedFilters} dataTestBlock='workflow-runs-view'>
            <NullStateWorkflowRuns />
            <Spacer height={3} />
            <div>Monitor your workflows by checking their runs.</div>
            <Spacer height={5} />
          </TableNullState>
        }
        headerTR={
          <>
            {withSelectableRows && (
              <TableTH
                id='workflow-runs-table-v2__table-header-select-all-cell'
                onClick={(e: MouseEvent) => e.stopPropagation()}
                canSort={false}
              >
                <Checkbox
                  id='workflow-runs-table-v2__table-header-select-all-checkbox'
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  disabled={erroredWorkflowRuns.length === 0}
                />
              </TableTH>
            )}
            {columnConfigs.map(({ id, dataTest, header, sortParam }, i) => {
              if (sortParam) {
                return (
                  <TableTH
                    key={i}
                    onClick={() =>
                      handleSortUpdate({
                        sort: sortParam,
                        shouldSortDescending:
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          sortParam === sortParams.sort ? !sortParams.shouldSortDescending : true,
                      })
                    }
                    canSort={true}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    isSorted={sortParams.sort === sortParam}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    isDescending={sortParams.shouldSortDescending}
                    id={id}
                    data-test={dataTest}
                  >
                    <Cell text={header} />
                  </TableTH>
                );
              }
              return (
                <TableTH key={i} data-test={dataTest} id={id}>
                  <Cell text={header} />
                </TableTH>
              );
            })}
          </>
        }
      >
        {workflowRunRows}
      </TableLayout>
    </WorkflowRunsTableContainer>
  );
};

export default WorkflowRunsTable;
